import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DateTime, Info, Interval } from 'luxon';
import CalendarContext from '../CalendarContext';
import Square from '../Square';
import { Grid } from './styled';
const sortRange = (range) => range.sort((a, b) => a.getTime() - b.getTime());
function DayView() {
    const context = useContext(CalendarContext);
    const { date, range, max, min, setDate, disabledDate, setRange, onRangeChange, } = context;
    const today = useMemo(() => DateTime.now().startOf('day'), []);
    const [hovered, setHovered] = useState(null);
    const weekdays = useMemo(() => Info.weekdays('long').map((day) => (React.createElement(Square, { "$readOnly": true, key: day }, day[0]))), []);
    const [firstRangeDate, secondRangeDate] = useMemo(() => range, [range]);
    const getIsInRange = useCallback((current) => {
        const isSelectedValidRange = firstRangeDate && secondRangeDate;
        if (isSelectedValidRange) {
            return Interval.fromDateTimes(DateTime.fromJSDate(firstRangeDate), DateTime.fromJSDate(secondRangeDate).plus({ milliseconds: 1 })).contains(current);
        }
        const isNotSelecting = !(firstRangeDate && hovered);
        if (isNotSelecting) {
            return false;
        }
        // is selecting second date in range
        const interval = sortRange([firstRangeDate, hovered.toJSDate()]).map((item) => DateTime.fromJSDate(item));
        return Interval.fromDateTimes(interval[0], interval[1].plus({ milliseconds: 1 })).contains(current);
    }, [firstRangeDate, secondRangeDate, hovered]);
    const getIsSelected = useCallback((current) => (firstRangeDate &&
        current.toISODate() ===
            DateTime.fromJSDate(firstRangeDate).startOf('day').toISODate()) ||
        (secondRangeDate &&
            current.toISODate() ===
                DateTime.fromJSDate(secondRangeDate).startOf('day').toISODate()), [firstRangeDate, secondRangeDate]);
    const getIsDisabled = useCallback((current) => current > DateTime.fromJSDate(max) ||
        current < DateTime.fromJSDate(min) ||
        disabledDate(current.toJSDate()), [min, max, disabledDate]);
    const handleClick = useCallback((current) => () => {
        const isSelectingFirstDate = !firstRangeDate || (firstRangeDate && secondRangeDate);
        const isSelectingSecondDate = !!firstRangeDate && secondRangeDate === null;
        const isFirstDateSameAsCurrent = firstRangeDate &&
            current.toISODate() ===
                DateTime.fromJSDate(firstRangeDate).startOf('day').toISODate();
        if (isSelectingFirstDate) {
            setRange([current.toJSDate(), null]);
            return;
        }
        if (isFirstDateSameAsCurrent) {
            setRange([null, null]);
            return;
        }
        if (isSelectingSecondDate) {
            const newValidRange = sortRange([firstRangeDate, current.toJSDate()]);
            setRange(newValidRange);
            onRangeChange(newValidRange);
            setDate(current);
        }
    }, [firstRangeDate, secondRangeDate, setRange, setDate, onRangeChange]);
    const handleHover = useCallback((current) => () => {
        const isSelectingSecondDate = !!firstRangeDate && secondRangeDate === null;
        if (isSelectingSecondDate) {
            setHovered(current);
        }
        else {
            setHovered(null);
        }
    }, [firstRangeDate, secondRangeDate]);
    const previousMonthDays = useMemo(() => {
        const first = date.startOf('month');
        const weekday1st = date.startOf('month').weekday;
        return new Array(weekday1st - 1).fill('').map((_, index) => {
            const current = first.minus({ days: weekday1st - index - 1 });
            const label = current.toLocaleString(DateTime.DATE_HUGE);
            const key = current.toLocaleString();
            return (React.createElement(Square, { key: key, "aria-label": label, "$gray": true, disabled: getIsDisabled(current), "$altDisabled": getIsDisabled(current), "$inRange": getIsInRange(current), onMouseEnter: handleHover(current), onMouseLeave: () => setHovered(null), onClick: handleClick(current), selected: getIsSelected(current) }, current.day));
        });
    }, [
        date,
        handleClick,
        handleHover,
        getIsInRange,
        getIsSelected,
        getIsDisabled,
    ]);
    const currentMonthDays = useMemo(() => {
        const first = date.startOf('month');
        return new Array(date.daysInMonth).fill('').map((_, index) => {
            const current = first.plus({ days: index });
            const label = current.toLocaleString(DateTime.DATE_HUGE);
            const key = current.toLocaleString();
            const isToday = current.toISODate() === today.toISODate();
            return (React.createElement(Square, { key: key, "aria-label": label, "$today": isToday, selected: getIsSelected(current), disabled: getIsDisabled(current), "$altDisabled": getIsDisabled(current), "$inRange": getIsInRange(current), onMouseEnter: handleHover(current), onMouseLeave: () => setHovered(null), onClick: handleClick(current) }, current.day));
        });
    }, [
        date,
        today,
        handleClick,
        handleHover,
        getIsInRange,
        getIsSelected,
        getIsDisabled,
    ]);
    const nextMonthDays = useMemo(() => {
        const last = date.endOf('month').startOf('day');
        const weekdayLast = last.weekday;
        return new Array(7 - weekdayLast).fill('').map((_, index) => {
            const current = last.plus({ days: index + 1 });
            const label = current.toLocaleString(DateTime.DATE_HUGE);
            const key = current.toLocaleString();
            return (React.createElement(Square, { "$gray": true, key: key, "aria-label": label, disabled: getIsDisabled(current), "$altDisabled": getIsDisabled(current), selected: getIsSelected(current), "$inRange": getIsInRange(current), onMouseEnter: handleHover(current), onMouseLeave: () => setHovered(null), onClick: handleClick(current) }, current.day));
        });
    }, [
        date,
        handleClick,
        handleHover,
        getIsInRange,
        getIsSelected,
        getIsDisabled,
    ]);
    return (React.createElement(Grid, { view: "day" },
        weekdays,
        previousMonthDays,
        currentMonthDays,
        nextMonthDays));
}
export default DayView;
